import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, UseFormSetValue, UseFormWatch, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { v4 as uuid } from 'uuid';

import { DataTable, type DataTableColumnDef } from '@column/ui';

import { Asterix } from '../_components/Asterix';
import { AuthorizationCell } from '../_components/AuthorizationCell';
import { CounterCell } from '../_components/CounterCell';
import { ReviewFormValues, TransferState, TransferTemplateForm } from '../types';
import { BankAccountCell, CounterpartyCell, FormCell, StateCell, StateCellOption } from '~/components/Table';
import { useCreateACHTransfer } from '~/hooks/useTransfers';
import { useUpdateTransferTemplate } from '~/hooks/useTransferTemplates';
import { Action } from '~/lib/authz';
import { AchTransferTemplateResponse } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { buildAchTransferUpdateRequest } from './api';
import { getColumnVisibility } from './shared';

export const defaultACHColumns = ['amount', 'counterpartyId', 'type', 'bankAccountId', 'description', 'entryClassCode'];

export const isACHTransferAuthorized = (
  row: AchTransferTemplateResponse,
  isAuthorized: (action: Action) => boolean
) => {
  return row.bankAccountId && row.type
    ? isAuthorized({
        permission: row.type === 'CREDIT' ? 'achCredits' : 'achDebits',
        permissionLevel: 'write',
        bankAccount: { id: row.bankAccountId },
      })
    : true;
};

export const buildACHEditColumns = (
  setValue: UseFormSetValue<TransferTemplateForm>
): DataTableColumnDef<AchTransferTemplateResponse & { isAuthorized?: boolean }>[] => [
  {
    accessor: 'isAuthorized',
    header: '',
    cell: ({ value }) => <AuthorizationCell isAuthorized={value} />,
    width: '32px',
    sticky: 'left',
  },
  {
    accessor: 'amount',
    header: 'Amount',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.amount`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.AmountCell
            isEditable
            currency="USD"
            value={value}
            onValueChange={(amount) => onChange(amount && amount > 0 ? amount : undefined)}
          />
        )}
      </FormCell>
    ),
    width: '112px',
    sticky: 'left',
  },
  {
    accessor: 'counterpartyId',
    header: (
      <>
        Counterparty
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Counterparty',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.counterpartyId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <CounterpartyCell
            isEditable
            value={value}
            onChange={({ description, id }) => {
              onChange(id);
              setValue(`achTransfers.${rowIndex}.counterpartyDescription`, description);
            }}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 2fr)',
    sticky: 'left',
  },
  {
    accessor: 'type',
    header: (
      <>
        Type
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Type',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.type`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable
            value={value}
            onValueChange={onChange}
            options={[
              { label: 'Debit', value: 'DEBIT' },
              { label: 'Credit', value: 'CREDIT' },
            ]}
          />
        )}
      </FormCell>
    ),
    width: '120px',
  },
  {
    accessor: 'bankAccountId',
    header: (
      <>
        Bank Account
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Bank Account',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.bankAccountId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <BankAccountCell
            isEditable
            value={value}
            onChange={({ description, id }) => {
              onChange(id);
              setValue(`achTransfers.${rowIndex}.bankAccountDescription`, description);
            }}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 2fr)',
  },
  {
    accessor: 'description',
    header: 'Description',
    cell: ({ rowIndex, value: defaultValue }) => {
      return (
        <FormCell id={`achTransfers.${rowIndex}.description`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <DataTable.TextCell isEditable value={value} placeholder="Description" onValueChange={onChange} />
          )}
        </FormCell>
      );
    },
    width: 'minmax(180px, 2fr)',
  },
  {
    accessor: 'entryClassCode',
    header: (
      <>
        Entry Class Code
        <Asterix>*</Asterix>
      </>
    ),
    label: 'Entry Class Code',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.entryClassCode`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable
            placeholder="Please select"
            value={value}
            onValueChange={(newValue) => onChange(newValue)}
            options={['CCD', 'CIE', 'CTX', 'PPD', 'TEL', 'WEB'].map((code: string) => ({ label: code, value: code }))}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
  {
    accessor: 'allowOverdraft',
    header: 'Allow Overdraft',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.allowOverdraft`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable
            placeholder="Please select"
            value={String(typeof value !== 'boolean' ? true : value)}
            onValueChange={(newValue) => onChange(newValue === 'true')}
            options={[
              { label: 'Allow', value: 'true' },
              { label: 'Deny', value: 'false' },
            ]}
          />
        )}
      </FormCell>
    ),
    width: '144px',
  },
  {
    accessor: 'companyName',
    header: 'Company Name',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.companyName`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell isEditable value={value} placeholder="Company Name" onValueChange={onChange} />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
  {
    accessor: 'companyDiscretionaryData',
    header: 'Company Discretionary',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.companyDiscretionaryData`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable
            value={(value as string) ?? ''}
            placeholder="Company Discretionary Data"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 1fr)',
  },
  {
    accessor: 'companyEntryDescription',
    header: 'Company Entry Description',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.companyEntryDescription`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable
            value={(value as string) ?? ''}
            placeholder="Company Entry Description"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(220px, 1fr)',
  },
  {
    accessor: 'paymentRelatedInfo',
    header: 'Payment Related Info',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.paymentRelatedInfo`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable
            value={(value as string) ?? ''}
            placeholder="Payment Related Info"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 1fr)',
  },
  {
    accessor: 'receiverName',
    header: 'Receiver Name',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.receiverName`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable
            value={(value as string) ?? ''}
            placeholder="Receiver Name"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
  {
    accessor: 'receiverId',
    header: 'Receiver ID',
    cell: ({ rowIndex, value: defaultValue }) => (
      <FormCell id={`achTransfers.${rowIndex}.receiverId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable
            value={(value as string) ?? ''}
            placeholder="Receiver ID"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
];

export const achReviewColumns: DataTableColumnDef<
  Partial<AchTransferTemplateResponse> & { id: string; state: TransferState }
>[] = [
  {
    accessor: 'id',
    header: '',
    cell: ({ rowIndex }) => <CounterCell value={rowIndex + 1} />,
    width: '40px',
    sticky: 'left',
  },
  {
    accessor: 'state',
    header: '',
    cell: ({ value }) => (
      <StateCell state={value.type as StateCellOption} message={value.type === 'error' ? value.message : undefined} />
    ),
    width: '40px',
    sticky: 'left',
  },
  {
    accessor: 'amount',
    header: 'Amount',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.amount`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.AmountCell
            isEditable={row.state.type === 'error'}
            value={value}
            currency="USD"
            onValueChange={(amount) => onChange(amount && amount > 0 ? amount : undefined)}
          />
        )}
      </FormCell>
    ),
    width: '112px',
    sticky: 'left',
  },
  {
    accessor: 'counterpartyId',
    header: 'Counterparty',
    cell: ({ value: defaultValue, row, rowIndex }) =>
      row.state.type !== 'error' && row.counterpartyDescription ? (
        <DataTable.TextCell value={row.counterpartyDescription} />
      ) : (
        <FormCell id={`achTransfers.${rowIndex}.counterpartyId`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <CounterpartyCell isEditable={row.state.type === 'error'} value={value} onChange={(v) => onChange(v.id)} />
          )}
        </FormCell>
      ),
    width: 'minmax(180px, 2fr)',
    sticky: 'left',
  },
  {
    accessor: 'type',
    header: 'Type',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.type`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable={row.state.type === 'error'}
            value={value as any}
            onValueChange={(v) => onChange(v as any)}
            options={[
              { label: 'Debit', value: 'DEBIT' },
              { label: 'Credit', value: 'CREDIT' },
            ]}
          />
        )}
      </FormCell>
    ),
    width: '120px',
  },
  {
    accessor: 'bankAccountId',
    header: 'Bank Account',
    cell: ({ value: defaultValue, row, rowIndex }) =>
      row.state.type !== 'error' && row.bankAccountDescription ? (
        <DataTable.TextCell value={row.bankAccountDescription} />
      ) : (
        <FormCell id={`achTransfers.${rowIndex}.bankAccountId`} defaultValue={defaultValue}>
          {({ value, onChange }) => (
            <BankAccountCell
              isEditable={row.state.type === 'error'}
              value={(value as string) ?? ''}
              onChange={(v) => onChange(v.id)}
            />
          )}
        </FormCell>
      ),
    width: 'minmax(180px, 2fr)',
  },
  {
    accessor: 'description',
    header: 'Description',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.description`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Description"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 2fr)',
  },
  {
    accessor: 'entryClassCode',
    header: 'Entry Class Code',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.entryClassCode`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable={row.state.type === 'error'}
            value={value as any}
            onValueChange={(v) => onChange(v as any)}
            options={['CCD', 'CIE', 'CTX', 'PPD', 'TEL', 'WEB'].map((code: string) => ({ label: code, value: code }))}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 1fr)',
  },
  {
    accessor: 'allowOverdraft',
    header: 'Allow Overdraft',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.allowOverdraft`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.SelectCell
            isEditable={row.state.type === 'error'}
            value={String((value as boolean) ?? false)}
            onValueChange={(v) => onChange(v === 'true')}
            options={['Allow', 'Deny'].map((code: string) => ({ label: code, value: String(code === 'Allow') }))}
          />
        )}
      </FormCell>
    ),
    width: '144px',
  },
  {
    accessor: 'companyName',
    header: 'Company Name',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.companyName`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Company Name"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
  {
    accessor: 'companyDiscretionaryData',
    header: 'Company Discretionary',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.companyDiscretionaryData`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Company Discretionary"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(200px, 1fr)',
  },
  {
    accessor: 'companyEntryDescription',
    header: 'Company Entry Description',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.companyEntryDescription`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Company Entry Description"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(220px, 1fr)',
  },
  {
    accessor: 'paymentRelatedInfo',
    header: 'Payment Related Info',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.paymentRelatedInfo`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Payment Related Info"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(180px, 1fr)',
  },
  {
    accessor: 'receiverName',
    header: 'Receiver Name',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.receiverName`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Receiver Name"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
  {
    accessor: 'receiverId',
    header: 'Receiver ID',
    cell: ({ value: defaultValue, row, rowIndex }) => (
      <FormCell id={`achTransfers.${rowIndex}.receiverId`} defaultValue={defaultValue}>
        {({ value, onChange }) => (
          <DataTable.TextCell
            isEditable={row.state.type === 'error'}
            value={(value as string) ?? ''}
            placeholder="Receiver ID"
            onValueChange={onChange}
          />
        )}
      </FormCell>
    ),
    width: 'minmax(160px, 1fr)',
  },
];

export const useACHTransfersEditTable = () => {
  const { isAuthorized } = useSessionStore();
  const { control, setValue, watch, resetField } = useFormContext<TransferTemplateForm>();
  const achTransfers = watch('achTransfers');
  const { append, remove } = useFieldArray({
    control,
    name: 'achTransfers',
  });
  const transfersWithPermission = useMemo(
    () =>
      (achTransfers ?? []).map((transfer) => ({
        ...transfer,
        isAuthorized: isACHTransferAuthorized(transfer, isAuthorized),
      })),
    [JSON.stringify(achTransfers), isAuthorized]
  );
  const allTransfersAuthorized = transfersWithPermission.every((transfer) => transfer.isAuthorized);
  const achColumns = useMemo(() => buildACHEditColumns(setValue), [setValue]);

  const visibleColumns = watch('visibleColumns');
  const achColumnVisibility = useMemo(
    () => ({
      ...getColumnVisibility(achColumns, visibleColumns),
      isAuthorized: !allTransfersAuthorized,
    }),
    [achColumns, allTransfersAuthorized, visibleColumns]
  );

  const achAppend = useCallback(() => {
    append({
      transferTemplateRowId: uuid(),
      amount: undefined,
      counterpartyId: undefined,
      type: watch('achDefaultParameters.type') ?? 'DEBIT',
      bankAccountId: watch('achDefaultParameters.bankAccountId'),
      description: undefined,
      entryClassCode: watch('achDefaultParameters.entryClassCode') ?? 'CCD',
      allowOverdraft: true,
    });
  }, [append, watch]);

  const achRemove = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }
      const index = achTransfers?.findIndex((transfer) => transfer.transferTemplateRowId === id) ?? -1;
      if (index === -1) {
        return;
      }
      remove(index);
      resetField('achTransfers', {
        defaultValue: (achTransfers ?? []).filter((_, i) => i !== index),
        keepDirty: true,
        keepError: true,
        keepTouched: true,
      });
    },
    [remove, resetField, achTransfers]
  );

  return {
    append: achAppend,
    remove: achRemove,
    data: transfersWithPermission,
    columns: achColumns,
    columnVisibility: achColumnVisibility,
  };
};

export const useACHTransfersReviewTable = ({
  control,
  watch,
  transferTemplateId,
}: {
  control: Control<ReviewFormValues>;
  watch: UseFormWatch<ReviewFormValues>;
  transferTemplateId: string;
}) => {
  const { fields: transfers, update } = useFieldArray({ control, name: 'achTransfers' });
  const [states, setStates] = useState<TransferState[]>([]);
  const { addDangerNotification } = useNotificationStore();
  const { createRequest: createACHTransfer } = useCreateACHTransfer();
  const { createRequest: updateTransferTemplate } = useUpdateTransferTemplate();

  useEffect(() => {
    setStates((prev) =>
      prev.length === transfers.length ? prev : Array.from({ length: transfers.length }, () => ({ type: 'idle' }))
    );
  }, [transfers.length]);

  const setState = useCallback((index: number, state: TransferState) => {
    setStates((prev) => prev.map((s, i) => (i === index ? state : s)));
  }, []);

  const handleRow = useCallback(
    async (index: number): Promise<boolean> => {
      const currentState = states[index] || { type: 'idle' };
      if (currentState.type === 'success') return false;

      setState(index, { type: 'loading' });
      try {
        const achData = watch(`achTransfers.${index}`) as AchTransferTemplateResponse;
        const idempotencyKeyValue = uuid();

        await createACHTransfer({
          amount: `${achData.amount}`,
          counterpartyId: achData.counterpartyId!,
          type: achData.type as any,
          bankAccountId: achData.bankAccountId,
          description: achData.description,
          entryClassCode: achData.entryClassCode as any,
          allowOverdraft: achData.allowOverdraft,
          companyName: achData.companyName,
          companyDiscretionaryData: achData.companyDiscretionaryData,
          companyEntryDescription: achData.companyEntryDescription,
          paymentRelatedInfo: achData.paymentRelatedInfo,
          receiverName: achData.receiverName,
          receiverId: achData.receiverId,
          currencyCode: 'USD',
          idempotencyKey: idempotencyKeyValue,
        });

        setState(index, { type: 'success' });
        return true;
      } catch (e: any) {
        const errorMessage = e?.message ? `${e.message} ${e?.details?.reason ?? ''}` : 'Unknown error occurred';
        setState(index, { type: 'error', message: errorMessage });
        addDangerNotification({
          content: (
            <>
              <span className="underline">{index + 1}. Transfer</span> {errorMessage}
            </>
          ),
          timeout: 8000,
        });
        return false;
      }
    },
    [states, setState, watch, createACHTransfer, addDangerNotification]
  );

  const applyErrorCorrectionsAndUpdateTemplate = useCallback(async () => {
    const curData = watch('achTransfers');
    const updateRequest = buildAchTransferUpdateRequest(curData);
    const updatedTemplate = await updateTransferTemplate({
      transferTemplateId,
      achTransfers: updateRequest,
    });
    if (!updatedTemplate) return [] as AchTransferTemplateResponse[];

    const updatedAchTransfers = updatedTemplate.achTransfers || [];
    updatedAchTransfers.forEach((updatedTransfer: AchTransferTemplateResponse, i: number) => {
      const originalTransfer = curData[i];
      update(i, {
        ...originalTransfer,
        bankAccountDescription: updatedTransfer.bankAccountDescription,
        counterpartyDescription: updatedTransfer.counterpartyDescription,
      });
    });
    return updatedAchTransfers as AchTransferTemplateResponse[];
  }, [watch, transferTemplateId, updateTransferTemplate, update]);

  const visibleColumns = useWatch({ control, name: 'visibleColumns' });
  const achColumnVisibility = useMemo(
    () => ({ ...getColumnVisibility(achReviewColumns, visibleColumns), id: true, state: true }),
    [visibleColumns]
  );

  const data = useMemo(
    () =>
      transfers.map((transfer, i) => ({
        ...transfer,
        id: transfer.id ?? String(i),
        state: states[i] ?? ({ type: 'idle' } as TransferState),
      })),
    [transfers, states]
  );

  return {
    columns: achReviewColumns,
    columnVisibility: achColumnVisibility,
    data,
    states,
    handleRow,
    applyErrorCorrectionsAndUpdateTemplate,
  };
};
